import * as Yup from "yup";
import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import {
  CREATE_TAX_COMPONENT,
  GET_TAX_COMPONENT_BY_ID,
  GLOBAL_DROP_DOWN,
  UPDATE_TAX_COMPONENT,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { IOption } from "../../data/AppInterface";

export interface ITaxComponentForm {
  id?: string;
  name: string;
  rate: number | null;
  tax_category_id: string | null;
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
}

export const TAX_COMPONENT_INITIAL_VALUES = {
  name: "",
  tax_category_id: null,
  rate: null,
};

export const taxComponentValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    name: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("name")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("name")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("name")],
          ["min", "2"],
        ])
      ),
    rate: Yup.number()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("rate")],
        ])
      )
      .max(
        500,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("rate")],
          ["max", "500"],
        ])
      )
      .min(
        0.001,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("rate")],
          ["min", "0.001"],
        ])
      ),
    tax_category_id: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("tax_category")],
      ])
    ),
  });

export const fetchTaxComponentById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setTaxComponents: TReactSetState<ITaxComponentForm | undefined>
) => {
  try {
    const taxComponentsFieldData = await GET_TAX_COMPONENT_BY_ID(id);
    if (
      taxComponentsFieldData &&
      taxComponentsFieldData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setTaxComponents(taxComponentsFieldData.data);
    } else {
      handleClose(false);
      sweetAlertError({
        message:
          taxComponentsFieldData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchTaxComponentById");
  }
};

export const handleSubmitTaxComponent = async (
  values: ITaxComponentForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void
) => {
  setLoading(true);
  try {
    let resultTaxComponents;
    if (id) {
      resultTaxComponents = await UPDATE_TAX_COMPONENT(values, id);
    } else {
      resultTaxComponents = await CREATE_TAX_COMPONENT(values);
    }
    if (
      resultTaxComponents &&
      resultTaxComponents.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      sweetAlertSuccess({
        message: resultTaxComponents.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: resultTaxComponents?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitTaxComponent");
  } finally {
    setLoading(false);
  }
};

export const getTaxCategoryDdl = async (
  setTaxCategory: TReactSetState<IOption<number>[]>
) => {
  try {
    const payload = {
      entity: "tax_category",
      data_value: "id",
      display_value: "category_name",
      filters: [
        {
          field: "firm_id",
          value: null,
          condition: "eq",
        },
      ],
    };
    const taxCategoryDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      taxCategoryDDLList &&
      taxCategoryDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (taxCategoryDDLList.data) {
        setTaxCategory(taxCategoryDDLList.data);
      } else {
        setTaxCategory([]);
      }
    } else {
      toastError(taxCategoryDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getTaxCategoryDdl");
  }
};
